import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "sonner";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import Home from "./pages/Home";
import Classes from "./pages/Classes";
import ClassDetail from "./pages/ClassDetail";
import CampWeek from "./pages/CampWeek";
import RsvpTracker from "./pages/RsvpTracker";
import Tutorials from "./pages/Tutorials";
import TutorialDetail from "./pages/TutorialDetail";
import Studio from "./pages/Studio";
import About from "./pages/About";
import Login from "./pages/Login";
import Register from "./pages/Register";
import SetPassword from "./pages/SetPassword";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import Admin from "./pages/Admin";
import { PaymentSuccess, PaymentCancel } from "./pages/Payment";
import Gift from "./pages/Gift";
import Membership from "./pages/Membership";
import OpenStudio from "./pages/OpenStudio";
import Parties from "./pages/Parties";
import PartyDeposit from "./pages/PartyDeposit";
import PartyPayment from "./pages/PartyPayment";
import FinalPartyDetails from "./pages/FinalPartyDetails";
import Shop from "./pages/Shop";
import Cart from "./pages/Cart";
import { CartProvider } from "./context/CartContext";
import { CreatorsList, CreatorDetail, CreatorEdit } from "./pages/Creators";
import { trackPageView } from "./lib/analytics";
import "./App.css";

function Protected({ children, adminOnly = false }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <div className="pt-40 text-center text-stone-500">Loading…</div>;
  if (!user || user === false) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname)}`} replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
}

function ScrollToTop() {
  const { pathname, search } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    trackPageView(pathname);
    try {
      const code = new URLSearchParams(search).get("coupon");
      if (code) localStorage.setItem("cs_pending_coupon", code.trim().toUpperCase());
    } catch { /* ignore */ }
  }, [pathname, search]);
  return null;
}

function Shell() {
  return (
    <div className="App min-h-screen flex flex-col bg-[#FAFAF9]">
      <ScrollToTop />
      <Navbar />
      <main className="flex-1">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/classes" element={<Classes />} />
          <Route path="/classes/:id" element={<ClassDetail />} />
          <Route path="/camps/:slug" element={<CampWeek />} />
          <Route path="/rsvp/:token" element={<RsvpTracker />} />
          <Route path="/tutorials" element={<Tutorials />} />
          <Route path="/tutorials/:id" element={<TutorialDetail />} />
          <Route path="/studio" element={<Navigate to="/guest-creators" replace />} />
          <Route path="/guest-creators" element={<Studio />} />
          <Route path="/about" element={<About />} />
          <Route path="/gift" element={<Gift />} />
          <Route path="/membership" element={<Membership />} />
          <Route path="/open-studio" element={<OpenStudio />} />
          <Route path="/parties" element={<Parties />} />
          <Route path="/party-deposit/:id" element={<PartyDeposit />} />
          <Route path="/party-payment/:id" element={<PartyPayment />} />
          <Route path="/final-details/:id" element={<FinalPartyDetails />} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/creators" element={<CreatorsList />} />
          <Route path="/creators/:id" element={<CreatorDetail />} />
          <Route path="/creator/edit" element={<Protected><CreatorEdit /></Protected>} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/set-password" element={<SetPassword />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
          <Route path="/admin" element={<Protected adminOnly><Admin /></Protected>} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
        </Routes>
      </main>
      <Footer />
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <BrowserRouter>
          <Shell />
        </BrowserRouter>
      </CartProvider>
    </AuthProvider>
  );
}
